import { Component, DestroyRef, OnInit, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  UntypedFormGroup,
  Validators,
  UntypedFormBuilder,
  ReactiveFormsModule,
} from '@angular/forms';
import { IDeclarantRate } from '../../../../../@interfaces/declarant-rate.interface';
import { IDeclarantTypes } from '../../../../../@interfaces/declarantTypes.interface';
import { ITypes } from '../../../../../@interfaces/types.interface';
import { ProviderService } from '../../../../../@pages/provider/provider.service';
import { TributaryService } from '../../../../../@pages/tributary/tributary.service';
import { DeclarantRateService } from '../../../../services/declarant-rate.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { NgxSpinnerService, NgxSpinnerModule } from 'ngx-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AlertService } from '@shared-services';

@Component({
  selector: 'app-modify-declarant-rate',
  templateUrl: './modify-declarant-rate.component.html',
  styleUrls: ['./modify-declarant-rate.component.css'],
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    MatButtonModule,
    NgxSpinnerModule,
    TranslatePipe,
  ],
})
export class ModifyDeclarantRateComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private providerService = inject(ProviderService);
  private tributaryService = inject(TributaryService);
  private declarantRateService = inject(DeclarantRateService);
  private translate = inject(TranslateService);
  private spinnerService = inject(NgxSpinnerService);
  private alertService = inject(AlertService);
  private destroyRef = inject(DestroyRef);
  private dialogRef = inject(MatDialogRef<ModifyDeclarantRateComponent>);

  readonly rate = input<IDeclarantRate>({} as IDeclarantRate);

  declarantRateForm: UntypedFormGroup = this.fb.group({
    value: ['', [Validators.required, Validators.min(0)]],
    uvt: ['', [Validators.required, Validators.min(0)]],
  });
  declarantName = '';
  typeDescription = '';

  ngOnInit(): void {
    const row = this.rate();
    this.declarantRateForm.patchValue({ value: row.value, uvt: row.uvt });
    const targetDeclarant = Number(row.id_declarant);
    const targetType = Number(row.id_type);
    this.providerService
      .getAllDeclarantTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: IDeclarantTypes[]) => {
        const match = (result || []).find(
          (declarant) => Number(declarant.id_declarant) === targetDeclarant,
        );
        this.declarantName = match
          ? `${this.translate.instant(match.name)} - ${this.translate.instant(match.type)}`
          : '';
      });
    this.tributaryService
      .getAllTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: ITypes[]) => {
        const match = (result || []).find(
          (type) => Number(type.id_type) === targetType,
        );
        this.typeDescription = match ? match.description : '';
      });
  }

  textValid(text: string) {
    return (
      this.declarantRateForm.get(text)?.invalid &&
      this.declarantRateForm.get(text)?.touched
    );
  }

  update() {
    this.declarantRateForm.markAllAsTouched();
    if (this.declarantRateForm.invalid === true) {
      this.openAlert(
        'error',
        'tributary.common.error',
        'tributary.declarantRate.messages.requiredFields',
      );
      return;
    }
    this.spinnerService.show();
    this.saveOnDataBase();
  }

  saveOnDataBase() {
    this.declarantRateService
      .updateDeclarantRate({
        id: this.rate().id,
        value: Number(this.declarantRateForm.value.value),
        uvt: Number(this.declarantRateForm.value.uvt),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.spinnerService.hide();
          if (!result) {
            this.openAlert(
              'error',
              'tributary.common.error',
              'tributary.declarantRate.messages.genericError',
            );
            return;
          }
          this.alertService
            .open({
              title: this.translate.instant('tributary.common.success'),
              message: this.translate.instant(
                'tributary.declarantRate.messages.updated',
              ),
              showClose: true,
              type: 'success',
            })
            .subscribe({
              accept: () => this.dialogRef.close(true),
              close: () => this.dialogRef.close(true),
            });
        },
        error: (error) => {
          this.spinnerService.hide();
          const code = error?.graphQLErrors?.[0]?.extensions?.code;
          const isDuplicate = code === 'DECLARANT_RATE_ALREADY_EXISTS';
          this.openAlert(
            isDuplicate ? 'warning' : 'error',
            isDuplicate
              ? 'tributary.declarantRate.titles.notCreated'
              : 'tributary.common.error',
            isDuplicate
              ? 'tributary.declarantRate.messages.duplicate'
              : 'tributary.declarantRate.messages.genericError',
          );
        },
      });
  }

  private openAlert(
    type: 'success' | 'error' | 'warning',
    titleKey: string,
    messageKey: string,
  ): void {
    this.alertService.open({
      title: this.translate.instant(titleKey),
      message: this.translate.instant(messageKey),
      showClose: true,
      type,
    });
  }
}
